import { navigate } from '@reach/router';
import { Formik, FormikHelpers } from 'formik';
import React, { useRef, useState } from 'react';
import { useApiRequest } from '../../../api/useApiRequest';
import { AssertUserHasPermission } from '../../../authorisation/AssertUserHasPermission';
import { RouteComponentProps } from '../../../helpers/RouteComponentProps';
import { urls } from '../../../helpers/urls';
import { BreadcrumbBar, BreadcrumbLink, BreadcrumbSeparator } from '../../../layout/Breadcrumbs';
import { PageContainer } from '../../../layout/Layout';
import { FormErrorBox } from '../../../shared/form/Form';
import { Header1 } from '../../../shared/Headers';
import { Project, ProjectHeader } from '../../projects/project-section/ProjectHeader';
import { projectUrls } from '../../projects/projectUrls';
import { GetDataForQuoteOrDesignForm } from '../../shared/quote-or-design-form/GetDataForQuoteOrDesignForm';
import { CreateQuoteResponse } from '../createQuoteResponse';
import { GenerateBillOfMaterialsForQuoteModal } from '../GenerateBillOfMaterialsForQuoteModal';
import { CreateUpdateQuoteCommand, mapQuoteFormModelToCommand } from '../createUpdateQuoteDto';
import { navigateAfterSavingQuote } from '../quote-form/navigateAfterSavingQuote';
import { NextStepAfterSavingQuote, QuoteForm } from '../quote-form/QuoteForm';
import { QuoteFormModel, quoteFormModelValidator } from '../quote-form/quoteFormModel';
import { mapQuoteResponseToQuoteFormModel, QuoteResponse } from '../quoteResponse';
import { GetRequest } from '../../../shared/GetRequest';

type Props = { project: Project } & RouteComponentProps<{ projectId: string; quoteId: string }>;

export const CreateQuoteRevision = ({ project, quoteId }: Props) => {
  const quoteEndpointUrl = `projects/${project.projectId}/quotes/${quoteId}`;

  const getQuoteRequest = useApiRequest<QuoteResponse>({
    endpointUrl: quoteEndpointUrl,
    httpMethod: 'get',
  });

  const createQuoteRequest = useApiRequest<CreateQuoteResponse, CreateUpdateQuoteCommand>({
    httpMethod: 'post',
    endpointUrl: `projects/${project.projectId}/quotes`,
  });

  const [savedQuoteId, setSavedQuoteId] = useState<number | null>(null);
  const nextStepAfterSave = useRef<NextStepAfterSavingQuote>('BackToProject');

  const onSubmit = (formModel: QuoteFormModel, formikHelpers: FormikHelpers<QuoteFormModel>) => {
    const createQuoteCommand: CreateUpdateQuoteCommand = mapQuoteFormModelToCommand(formModel, project.projectId);

    if (createQuoteRequest.inProgress) {
      return;
    }

    return createQuoteRequest.sendRequest({
      requestBody: createQuoteCommand,
      onSuccess: response => {
        formikHelpers.setSubmitting(false);

        void navigateAfterSavingQuote(
          project.projectId,
          response.quoteId,
          createQuoteCommand.quote.isDraft,
          nextStepAfterSave.current,
          setSavedQuoteId,
        );
      },
      onFailure: () => {
        formikHelpers.setSubmitting(false);
      },
    });
  };

  return (
    <GetRequest getDataRequest={getQuoteRequest}>
      {({ response: quoteResponse }) => (
        <>
          {savedQuoteId != null && (
            <GenerateBillOfMaterialsForQuoteModal
              projectId={project.projectId}
              quoteId={savedQuoteId}
              isOpen={true}
              closeModal={() => navigate(projectUrls.editQuote(project.projectId, savedQuoteId))}
              shouldRegenerate={false}
            />
          )}
          <BreadcrumbBar>
            <BreadcrumbLink to={urls.home}>Projects</BreadcrumbLink>
            <BreadcrumbSeparator />
            <BreadcrumbLink to={projectUrls.projectDetails(project.projectId)}>
              {project.projectReference}
            </BreadcrumbLink>
            <BreadcrumbSeparator />
            <div>{quoteResponse.name}</div>
            <BreadcrumbSeparator />
            <div>Revise</div>
          </BreadcrumbBar>
          <ProjectHeader project={project} />
          <PageContainer>
            <AssertUserHasPermission requiredPermission="Edit">
              <GetDataForQuoteOrDesignForm project={project}>
                {partOptions => (
                  <>
                    <Header1>New Revision</Header1>
                    <Formik<QuoteFormModel>
                      onSubmit={onSubmit}
                      initialValues={mapQuoteResponseToQuoteFormModel(
                        quoteResponse,
                        partOptions,
                        project.buildTypeCode,
                      )}
                      validate={formModel => quoteFormModelValidator.validate(formModel)}
                    >
                      {formProps => (
                        <QuoteForm
                          {...formProps}
                          project={project}
                          dataForQuoteOrDesignForm={partOptions}
                          setNextStepAfterSave={nextStep => {
                            nextStepAfterSave.current = nextStep;
                          }}
                        />
                      )}
                    </Formik>
                    {createQuoteRequest.error && <FormErrorBox error={createQuoteRequest.error} />}
                  </>
                )}
              </GetDataForQuoteOrDesignForm>
            </AssertUserHasPermission>
          </PageContainer>
        </>
      )}
    </GetRequest>
  );
};
